import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Tag, Tooltip } from 'antd';

import {
  assignAssessmentNews,
  revokeAssessmentAssignment,
  selectors,
  AssessmentAssignment,
  AssessmentInstrument,
  displayNewsHeadline,
} from 'state';

import { BUCKET_ORDER } from './constants';

type NewsRow = {
  id: number;
  headline: string;
  rephrased_headline?: string | null;
};

// Client-side ids for optimistic assignments — always negative so they can
// never collide with server pks.
let tempIdSeq = -1;
const nextTempId = () => tempIdSeq--;

const zebraRowClass = (_: unknown, index: number) =>
  `assessment-news-row ${index % 2 === 0 ? 'news-row-light' : 'news-row-dark'}`;

const DRAG_TYPE = 'text/plain';

const Bucket: React.FC<{
  instrument: AssessmentInstrument;
  assignments: AssessmentAssignment[];
  headlines: { [newsId: number]: string };
  canWrite: boolean;
}> = ({ instrument, assignments, headlines, canWrite }) => {
  const dispatch = useDispatch();
  const [dragOver, setDragOver] = useState(0);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(0);
    if (!canWrite) return;
    const newsId = Number(e.dataTransfer.getData(DRAG_TYPE));
    if (!newsId) return;
    // One news item can sit in several buckets, but only once per bucket.
    if (assignments.some(a => a.news === newsId)) return;
    dispatch(
      assignAssessmentNews({
        news: newsId,
        instrument_code: instrument.code,
        temp_id: nextTempId(),
      }),
    );
  };

  const className = [
    'assessment-bucket',
    dragOver > 0 && canWrite ? 'assessment-bucket-over' : '',
    canWrite ? '' : 'assessment-bucket-disabled',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={className}
      onDragOver={e => e.preventDefault()}
      onDragEnter={() => setDragOver(c => c + 1)}
      onDragLeave={() => setDragOver(c => Math.max(0, c - 1))}
      onDrop={onDrop}
    >
      <div className="assessment-bucket-title">
        {instrument.code}
        <span className="assessment-bucket-kind">{instrument.kind}</span>
      </div>
      <div className="assessment-bucket-chips">
        {assignments.length === 0 && (
          <span className="assessment-bucket-hint">Drop news here</span>
        )}
        {assignments.map(assignment => {
          const headline =
            headlines[assignment.news] || `News #${assignment.news}`;
          const label =
            headline.length > 70 ? `${headline.slice(0, 70)}…` : headline;
          return (
            <Tooltip title={headline} key={assignment.id}>
              <Tag
                closable={canWrite && !assignment.pending}
                color={assignment.pending ? 'processing' : undefined}
                onClose={e => {
                  e.preventDefault();
                  dispatch(
                    revokeAssessmentAssignment({ assignment: assignment.id }),
                  );
                }}
              >
                {label}
              </Tag>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

/** The period's news feed (draggable rows) + the instrument buckets. */
const NewsBuckets: React.FC<{ canWrite: boolean }> = ({ canWrite }) => {
  const newsList = useSelector(selectors.newsList) as NewsRow[];
  const { fetching_news } = useSelector(selectors.activeSession);
  const instruments = useSelector(selectors.assessmentInstruments);
  const assignments = useSelector(selectors.assessmentAssignments);

  // The 8 underlyings, in the canonical order; options are not drop targets.
  const buckets = useMemo(() => {
    const underlyings = instruments.filter(i => i.kind !== 'option');
    return [...underlyings].sort(
      (a, b) =>
        (BUCKET_ORDER.indexOf(a.code) + 1 || 99) -
        (BUCKET_ORDER.indexOf(b.code) + 1 || 99),
    );
  }, [instruments]);

  const assignmentsByCode = useMemo(() => {
    const byCode: { [code: string]: AssessmentAssignment[] } = {};
    assignments.forEach(a => {
      if (!byCode[a.instrument_code]) byCode[a.instrument_code] = [];
      byCode[a.instrument_code].push(a);
    });
    return byCode;
  }, [assignments]);

  const headlines = useMemo(() => {
    const map: { [newsId: number]: string } = {};
    newsList.forEach(n => {
      map[n.id] = displayNewsHeadline(n);
    });
    return map;
  }, [newsList]);

  const columns = useMemo(
    () => [
      {
        title: 'Headline — drag onto an instrument',
        dataIndex: 'headline',
        key: 'headline',
        render: (_value: string, record: NewsRow) => (
          <div className="news-cell-wrap">{displayNewsHeadline(record)}</div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="assessment-mapping-grid">
      <div className="assessment-news-col">
        <Table<NewsRow>
          dataSource={newsList}
          bordered
          loading={fetching_news}
          rowKey="id"
          tableLayout="fixed"
          columns={columns}
          rowClassName={zebraRowClass}
          scroll={{ y: 480 }}
          pagination={false}
          style={{ border: '1px solid #efefef' }}
          onRow={record => ({
            draggable: canWrite,
            onDragStart: (e: React.DragEvent) =>
              e.dataTransfer.setData(DRAG_TYPE, String(record.id)),
          })}
        />
      </div>
      <div className="assessment-buckets-col">
        <div className="assessment-buckets-grid">
          {buckets.map(instrument => (
            <Bucket
              key={instrument.code}
              instrument={instrument}
              assignments={assignmentsByCode[instrument.code] || []}
              headlines={headlines}
              canWrite={canWrite}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewsBuckets;
